/**
 * AdminControlGestiones — Panel Gestor Senior / Admin para descargar en
 * lotes el "Control y gestiones" (jornada, kilometraje, horario laborable,
 * vacaciones) de cualquier usuario del gabinete. Pensado para auditorías
 * e Inspección de Trabajo.
 *
 * - Tabla con todos los usuarios (peritos, gestores, admins).
 * - Selección múltiple con checkboxes.
 * - Filtros por rol + búsqueda por nombre/email.
 * - El gestor decide qué secciones, qué formatos (PDF/Excel) y si incluir
 *   el resumen agregado del equipo.
 * - Endpoint backend: `POST /api/team/control-gestiones/export-bulk`.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { ROLES_APRUEBAN_MINUTA } from "@/lib/roles";
import {
  Users, MagnifyingGlass, DownloadSimple, Package, FilePdf, FileXls,
  Clock, MapPin, Briefcase, Calendar as CalIcon, CheckSquare, Square,
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "", label: "Todos los roles" },
  { value: "admin", label: "Administradores" },
  { value: "gestor_senior", label: "Gestores Senior" },
  { value: "gestor_siniestros", label: "Gestores de Siniestros" },
  { value: "perito_interno", label: "Peritos internos" },
  { value: "perito_externo", label: "Peritos externos" },
  { value: "tramitador_telematico", label: "Tramitadores telemáticos" },
];

const SECTION_LABELS = {
  jornada: { label: "Control horario", icon: Clock, tone: "brand" },
  km: { label: "Kilometraje", icon: MapPin, tone: "teal" },
  schedule: { label: "Horario laborable", icon: Briefcase, tone: "purple" },
  vacations: { label: "Vacaciones", icon: CalIcon, tone: "amber" },
};

function MONTH_NOW() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const MONTH_LABELS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
function fmtMonthES(yyyymm) {
  if (!yyyymm || yyyymm.length !== 7) return yyyymm;
  return `${MONTH_LABELS_ES[parseInt(yyyymm.slice(5, 7), 10) - 1]} de ${yyyymm.slice(0, 4)}`;
}

/** Descarga la respuesta como archivo manteniendo el filename del header. */
async function downloadBlob(req, fallbackName) {
  try {
    const r = await req;
    const cd = r.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const fname = m ? m[1] : fallbackName;
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    return true;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Error generando el archivo");
    return false;
  }
}

export default function AdminControlGestiones() {
  const { user } = useAuth();
  // Acceso restringido a admin + gestor_senior (mismo set que aprueban minutas).
  if (!user) return null;
  if (!ROLES_APRUEBAN_MINUTA.has(user.role)) {
    return <Navigate to="/inbox" replace />;
  }

  return <AdminControlGestionesInner />;
}

function AdminControlGestionesInner() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Opciones de exportación
  const [month, setMonth] = useState(MONTH_NOW());
  const [sections, setSections] = useState({ jornada: true, km: true, schedule: true, vacations: true });
  const [formats, setFormats] = useState({ pdf: true, xlsx: false });
  const [includeSummary, setIncludeSummary] = useState(true);
  const [bulking, setBulking] = useState(false);

  useEffect(() => {
    api.get("/team/control-gestiones/users")
      .then((r) => setUsers(r.data.users || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error cargando usuarios"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterRole && u.role !== filterRole) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, filterRole, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selectedIds.has(u.id));
  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((u) => next.delete(u.id));
      } else {
        filtered.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const sectionsList = Object.keys(sections).filter((k) => sections[k]);
  const formatsList = Object.keys(formats).filter((k) => formats[k]);
  const needsMonth = sections.jornada || sections.km;

  const onBulk = async () => {
    if (selectedIds.size === 0) { toast.error("Selecciona al menos un usuario"); return; }
    if (sectionsList.length === 0) { toast.error("Selecciona al menos una sección"); return; }
    if (formatsList.length === 0) { toast.error("Selecciona al menos un formato"); return; }
    if (needsMonth && (!month || month.length !== 7)) {
      toast.error("Indica el mes (YYYY-MM) para jornada/kilometraje"); return;
    }
    setBulking(true);
    const payload = {
      user_ids: Array.from(selectedIds),
      sections: sectionsList,
      formats: formatsList,
      month: needsMonth ? month : null,
      include_team_summary: includeSummary,
    };
    await downloadBlob(
      api.post("/team/control-gestiones/export-bulk", payload, { responseType: "blob" }),
      `control-gestiones-equipo-${month || "actual"}.zip`,
    );
    setBulking(false);
  };

  // Descarga individual rápida por fila (PDF) sin abrir otra pantalla.
  const onQuick = async (userId, section, format) => {
    if (["jornada", "km"].includes(section) && (!month || month.length !== 7)) {
      toast.error("Indica el mes (YYYY-MM) en la cabecera"); return;
    }
    const params = new URLSearchParams({
      user_id: userId, section, format,
    });
    if (["jornada", "km"].includes(section)) params.set("month", month);
    await downloadBlob(
      api.get(`/team/control-gestiones/export-individual?${params}`, { responseType: "blob" }),
      `${section}.${format}`,
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 md:py-6 space-y-5"
         data-testid="admin-control-gestiones-page">
      <Header total={users.length} selected={selectedIds.size} />

      <FilterBar
        month={month} setMonth={setMonth}
        filterRole={filterRole} setFilterRole={setFilterRole}
        search={search} setSearch={setSearch}
      />

      <UserTable
        users={filtered}
        loading={loading}
        selectedIds={selectedIds}
        allSelected={allFilteredSelected}
        onToggle={toggle}
        onToggleAll={toggleAllFiltered}
        onQuick={onQuick}
      />

      <BulkPanel
        sections={sections} setSections={setSections}
        formats={formats} setFormats={setFormats}
        includeSummary={includeSummary} setIncludeSummary={setIncludeSummary}
        month={month} needsMonth={needsMonth}
        selectedCount={selectedIds.size}
        onBulk={onBulk} bulking={bulking}
      />
    </div>
  );
}

// ===========================================================
// Sub-componentes
// ===========================================================

function Header({ total, selected }) {
  return (
    <header className="flex items-center gap-3">
      <span className="icon-chip icon-chip-brand"><Users className="w-4 h-4" weight="regular" /></span>
      <div className="flex-1">
        <h1 className="page-title">Control de gestiones del equipo</h1>
        <div className="help-text">
          {total} usuarios en el gabinete · {selected} seleccionados ·
          {" "}Genera packs PDF/Excel para Inspección de Trabajo o auditorías internas.
        </div>
      </div>
    </header>
  );
}

function FilterBar({ month, setMonth, filterRole, setFilterRole, search, setSearch }) {
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:px-5">
      <div className="grid grid-cols-12 gap-3 items-end">
        <div className="col-span-12 sm:col-span-3">
          <label className="field-label block mb-1">Mes (jornada y km)</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 data-testid="admin-cg-month" />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <label className="field-label block mb-1">Rol</label>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
                  data-testid="admin-cg-role">
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="col-span-12 sm:col-span-5">
          <label className="field-label block mb-1">Buscar</label>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text" placeholder="Nombre o email…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="!pl-9" data-testid="admin-cg-search"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function UserTable({ users, loading, selectedIds, allSelected, onToggle, onToggleAll, onQuick }) {
  if (loading) {
    return (
      <section className="bg-white rounded-[10px] border border-border p-8">
        <p className="help-text text-center">Cargando usuarios…</p>
      </section>
    );
  }
  if (!users.length) {
    return (
      <section className="bg-white rounded-[10px] border border-border p-8">
        <p className="help-text text-center">No hay usuarios que coincidan con el filtro.</p>
      </section>
    );
  }
  return (
    <section className="bg-white rounded-[10px] border border-border overflow-hidden"
             data-testid="admin-cg-table">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-background text-[11.5px] border-b border-border">
            <tr>
              <th className="text-left py-2 px-3 font-semibold text-muted w-10">
                <button onClick={onToggleAll} data-testid="admin-cg-toggle-all"
                        className="inline-flex items-center text-muted hover:text-foreground"
                        title={allSelected ? "Deseleccionar todos" : "Seleccionar todos"}>
                  {allSelected
                    ? <CheckSquare className="w-4 h-4" weight="fill" />
                    : <Square className="w-4 h-4" weight="regular" />}
                </button>
              </th>
              <th className="text-left py-2 px-3 font-semibold text-muted">Nombre</th>
              <th className="text-left py-2 px-3 font-semibold text-muted">Rol</th>
              <th className="text-left py-2 px-3 font-semibold text-muted hidden md:table-cell">Email</th>
              <th className="text-center py-2 px-3 font-semibold text-muted hidden lg:table-cell">Activo</th>
              <th className="text-right py-2 px-3 font-semibold text-muted">Descarga rápida</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const sel = selectedIds.has(u.id);
              return (
                <tr key={u.id}
                    className={`border-t border-border ${sel ? "bg-accent-amber-bg/30" : ""}`}>
                  <td className="py-1.5 px-3">
                    <button onClick={() => onToggle(u.id)}
                            data-testid={`admin-cg-toggle-${u.id}`}
                            className="inline-flex items-center text-muted hover:text-foreground">
                      {sel
                        ? <CheckSquare className="w-4 h-4 text-brand" weight="fill" />
                        : <Square className="w-4 h-4" weight="regular" />}
                    </button>
                  </td>
                  <td className="py-1.5 px-3 font-medium">{u.name}</td>
                  <td className="py-1.5 px-3"><RoleBadge role={u.role} label={u.role_label} /></td>
                  <td className="py-1.5 px-3 text-muted hidden md:table-cell">{u.email}</td>
                  <td className="py-1.5 px-3 text-center hidden lg:table-cell">
                    {u.active
                      ? <span className="status-pill status-pill-success">Sí</span>
                      : <span className="status-pill status-pill-gray">No</span>}
                  </td>
                  <td className="py-1.5 px-3 text-right">
                    <QuickActions userId={u.id} onQuick={onQuick} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RoleBadge({ role, label }) {
  const cls = {
    admin: "bg-foreground text-white",
    gestor_senior: "bg-brand text-white",
    gestor_siniestros: "bg-status-success-bg text-status-success-fg border border-status-success-bd",
    perito_interno: "bg-surface text-foreground border border-border",
    perito_externo: "bg-surface text-muted border border-border italic",
    tramitador_telematico: "bg-status-info-bg text-status-info-fg border border-status-info-bd",
  }[role] || "bg-surface text-foreground border border-border";
  return <span className={`status-pill ${cls}`}>{label}</span>;
}

function QuickActions({ userId, onQuick }) {
  // Menú compacto con un PDF rápido por sección (más usadas).
  return (
    <div className="inline-flex items-center gap-1">
      {Object.entries(SECTION_LABELS).map(([key, meta]) => {
        const Icon = meta.icon;
        return (
          <button key={key}
                  onClick={() => onQuick(userId, key, "pdf")}
                  data-testid={`admin-cg-quick-${userId}-${key}`}
                  className={`icon-chip icon-chip-${meta.tone} !w-7 !h-7`}
                  title={`Descargar ${meta.label} (PDF)`}>
            <Icon className="w-3.5 h-3.5" weight="regular" />
          </button>
        );
      })}
    </div>
  );
}

function BulkPanel({
  sections, setSections, formats, setFormats,
  includeSummary, setIncludeSummary,
  month, needsMonth, selectedCount, onBulk, bulking,
}) {
  return (
    <section className="bg-white rounded-[10px] border border-border p-4 md:p-5"
             data-testid="admin-cg-bulk-panel">
      <header className="flex items-center gap-2.5 mb-4">
        <span className="icon-chip icon-chip-amber"><Package className="w-4 h-4" weight="regular" /></span>
        <h2 className="section-title flex-1">Pack para Inspección de Trabajo</h2>
        <span className="text-[12px] text-muted">
          {selectedCount > 0
            ? `${selectedCount} usuario${selectedCount === 1 ? "" : "s"} seleccionado${selectedCount === 1 ? "" : "s"}`
            : "Sin selección"}
        </span>
      </header>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 md:col-span-5">
          <label className="field-label block mb-2">Secciones a incluir</label>
          <div className="space-y-2">
            {Object.entries(SECTION_LABELS).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <label key={key}
                       data-testid={`admin-cg-sec-${key}`}
                       className="flex items-center gap-2.5 text-[13px] cursor-pointer">
                  <input type="checkbox" checked={sections[key]}
                         onChange={(e) => setSections({ ...sections, [key]: e.target.checked })} />
                  <span className={`icon-chip icon-chip-${meta.tone} !w-6 !h-6`}>
                    <Icon className="w-3.5 h-3.5" weight="regular" />
                  </span>
                  <span>{meta.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="col-span-12 md:col-span-4">
          <label className="field-label block mb-2">Formato</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2.5 text-[13px] cursor-pointer"
                   data-testid="admin-cg-fmt-pdf">
              <input type="checkbox" checked={formats.pdf}
                     onChange={(e) => setFormats({ ...formats, pdf: e.target.checked })} />
              <FilePdf className="w-4 h-4 text-status-error-fg" weight="regular" /> PDF
            </label>
            <label className="flex items-center gap-2.5 text-[13px] cursor-pointer"
                   data-testid="admin-cg-fmt-xlsx">
              <input type="checkbox" checked={formats.xlsx}
                     onChange={(e) => setFormats({ ...formats, xlsx: e.target.checked })} />
              <FileXls className="w-4 h-4 text-status-success-fg" weight="regular" /> Excel
            </label>
          </div>
          <label className="flex items-center gap-2.5 text-[13px] cursor-pointer mt-4"
                 data-testid="admin-cg-team-summary">
            <input type="checkbox" checked={includeSummary}
                   onChange={(e) => setIncludeSummary(e.target.checked)} />
            <span>Incluir <b>resumen agregado</b> del equipo (PDF)</span>
          </label>
        </div>

        <div className="col-span-12 md:col-span-3 flex flex-col gap-2">
          <div className="text-[12px] text-muted">
            {needsMonth
              ? <>Mes seleccionado: <b>{fmtMonthES(month)}</b></>
              : <>El mes no aplica a las secciones elegidas.</>}
          </div>
          <button onClick={onBulk}
                  disabled={bulking || selectedCount === 0}
                  data-testid="admin-cg-bulk-download"
                  className="bg-foreground text-white text-[13px] px-4 h-10 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {bulking
              ? "Generando ZIP…"
              : <><DownloadSimple className="w-4 h-4" weight="bold" /> Descargar pack</>}
          </button>
          <p className="help-text italic">
            El ZIP se organiza por <code>Apellido_Nombre/</code> con un archivo por
            sección y formato. El resumen va en la raíz.
          </p>
        </div>
      </div>
    </section>
  );
}
